import { useState } from "react";
import { AdminModules } from "../../parameters/features";
import { DetailsView } from "./detailsView";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { useApolloQuery } from "../../../../hooks";
import { useAuthSession } from "../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../components/@common/NotificationsProvider";
import {
  ConfirmationDeleteModal,
  useLanguageTranslator,
} from "../../../../components";
import dayjs from "dayjs";
import { Filters } from "../../filters";
import { Stack } from "@mui/material";

const GET_INDICATORS_VALUES_WITH_PAGINATION = gql`
  query GetIndicatorValuesWithPagination(
    $page: Int!
    $countryId: Int
    $themeId: Int
    $indicatorId: Int
  ) {
    indicatorValuesWithPagination(
      page: $page
      countryId: $countryId
      themeId: $themeId
      indicatorId: $indicatorId
    ) {
      collection {
        country {
          id
          name
        }
        createdAt
        description
        id
        indicator {
          label
          id
        }
        source
        updatedAt
        value
        year
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const DELETE_INDICATOR_VALUES = gql`
  mutation DeleteIndicatorValue($id: ID!) {
    indicatorValueDelete(input: { id: $id }) {
      clientMutationId
      indicatorValue {
        id
      }
    }
  }
`;

function IndicatorValues() {
  const [filters, setFilters] = useState({
    countryId: "",
    themeId: "",
    indicatorLabelId: "",
  });
  const [isOpen, setIsOpen] = useState({ status: false, data: {} });
  const [page, setPage] = useState(1);
  const [isOpenDelete, setIsOpenDelete] = useState({ status: false, data: {} });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();
  const { user } = useAuthSession() as any;
  const { translator } = useLanguageTranslator();
  const notify = useNotify();

  const {
    data: indicatorsValuesList,
    loading,
    refetch,
  } = useApolloQuery({
    queryDocument: GET_INDICATORS_VALUES_WITH_PAGINATION,
    isPrivateCall: true,
    variables: {
      page: page,
      ...(filters?.indicatorLabelId && filters?.indicatorLabelId !== "0"
        ? { indicatorId: Number(filters?.indicatorLabelId) }
        : {}),
      ...(filters?.themeId && filters?.themeId !== "0"
        ? { themeId: Number(filters?.themeId) }
        : {}),
      ...(filters?.countryId && filters?.countryId !== "0"
        ? { countryId: Number(filters?.countryId) }
        : {}),
    },
  });

  const modifiedGetIndicatorsValuesList =
    indicatorsValuesList?.indicatorValuesWithPagination?.collection?.map(
      (item: any) => {
        return {
          sr: item?.id,
          id: item?.id,
          label: item?.indicator?.label,
          country: item?.country?.name,
          description: item?.description,
          source: item?.source,
          value: item?.value,
          createdAt: dayjs(item?.createdAt?.split("T")?.[0] || "").format(
            "DD.MM.YYYY"
          ),
          updatedAt: dayjs(item?.updatedAt?.split("T")?.[0] || "").format(
            "DD.MM.YYYY"
          ),
        };
      }
    );

  const [
    deleteIndicatorValue,
    { loading: deleteIndicatorValueLoading, error },
  ] = useMutation(DELETE_INDICATOR_VALUES, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  async function onClickHandler(type: string, clickedItem: any) {
    if (type === "view") {
      setIsOpen({ status: true, data: clickedItem });
      return;
    }
    if (type === "edit") {
      navigate(`/admin/indicators-values/update?id=${clickedItem}`);
      return;
    }

    if (type === "delete") {
      setIsOpenDelete({ status: true, data: { id: clickedItem } });
    }
  }

  function handleCancel() {
    setIsOpen({ status: false, data: {} });
  }

  async function onClickDeleteHandler(item: any) {
    const response = await deleteIndicatorValue({
      variables: { id: Number(item?.id) },
    });
    if (response?.data) {
      notify.success(`Indicator Value Deleted successfully!`);
      setIsOpenDelete({ status: false, data: {} });
      setIsOpen({ status: false, data: {} });
      setTimeout(async () => {
        await refetch();
      }, 1000);
    }
  }

  function handleDeleteClose() {
    setIsOpenDelete({ status: false, data: {} });
  }

  function onClickDetailHandler(item: any) {
    setIsOpenDelete({ status: true, data: { id: item?.id } });
  }

  const columns = [
    { id: "sr", label: "ID", minWidth: "100%", align: "left" },
    {
      id: "label",
      label: translator("Label"),
      minWidth: "100%",
      align: "left",
    },
    {
      id: "country",
      label: translator("Country"),
      minWidth: "100%",
      align: "left",
    },
    {
      id: "value",
      label: translator("Value"),
      minWidth: "100%",
      align: "left",
    },
    {
      id: "createdAt",
      label: translator("dashboardTableHead.Date Created"),
      minWidth: "100%",
      align: "left",
    },
    {
      id: "updatedAt",
      label: translator("dashboardTableHead.Date Updated"),
      minWidth: "100px",
      align: "left",
    },
    {
      id: "id",
      label: translator("dashboardTableHead.Action"),
      minWidth: "100%",
      align: "right",
    },
  ];

  return (
    <>
      <ConfirmationDeleteModal
        isOpen={isOpenDelete?.status}
        item={isOpenDelete?.data}
        onClickHandler={onClickDeleteHandler}
        handleClose={handleDeleteClose}
      />
      <DetailsView
        isOpen={isOpen?.status}
        item={isOpen?.data}
        handleCancel={handleCancel}
        onClickHandler={onClickDetailHandler}
      />
      <AdminModules
        title={translator("Indicators Values")}
        path="/admin/indicators-values/add-new"
        data={modifiedGetIndicatorsValuesList}
        moduleKey="country-module"
        page={page}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        setPage={setPage}
        onClickHandler={onClickHandler}
        metadata={indicatorsValuesList?.indicatorValuesWithPagination?.metadata}
        isView={true}
        tableColumns={columns}
        isAccounts={true}
      >
        <Stack direction={"row"} justifyContent={"center"} mt={1}>
          <Filters setFilters={setFilters} filters={filters} />
        </Stack>
      </AdminModules>
    </>
  );
}

export { IndicatorValues };
